<template>
  <div class="annotation-controls">
    <image-browser-zoom-control
      :imgNotFound="imgNotFound"
      :img="displayImgObject"
      :imgIsLoading="imgIsLoading"
      :transform="transform"
      :isFlipped="isFlipped"
      :isFlipped90="isFlipped90"
      :size="zoomControlSize"
      :fit="zoomControlFit"
      :height="zoomControlHeight"
      :mainViewCanvasSize="mainViewCanvasSize"
      @updateZoom="$emit('updateZoom', $event)"
      @updateCenter="$emit('updateCenter', $event)"
      @stopFitZoom="$emit('stopFitZoom')"/>
    <v-row no-gutters justify="center">
      <v-col cols="auto">
        <annotation-mode-button
          :image="coloredAssetUrls.erase"
          :activeImage="activeAnnotationIcons.erase"
          title="Delete Annotation(s)"
          @click="annotationModeButtonClick(annotationModes.Delete)"
          :buttonMode="annotationModes.Delete"
          :annotationMode="annotationMode"/>
      </v-col>
      <v-col cols="auto">
        <annotation-mode-button
          :image="coloredAssetUrls.magnify"
          :activeImage="activeAnnotationIcons.magnify"
          title="Magnify"
          @click="annotationModeButtonClick(annotationModes.Magnify)"
          :buttonMode="annotationModes.Magnify"
          :annotationMode="annotationMode"/>
      </v-col>
      <v-col cols="auto">
        <circle-image-button
          :active="isFlipped"
          :image="flipIcon"
          title="Indirect View (180°)"
          btnClass="primary"
          @click="toggleFlipped()"/>
      </v-col>
      <v-col cols="auto">
        <circle-image-button
          :active="isFlipped90"
          :image="flip90Icon"
          title="90° Rotation"
          btnClass="primary"
          @click="toggleFlipped90()"/>
      </v-col>
      <v-col cols="auto">
        <circle-image-button
          title="Fit Image in Window"
          :image="coloredAssetUrls.fitToWindow"
          btnClass="primary"
          @click="$emit('zoomToFit')"/>
      </v-col>
      <v-col cols="auto">
        <circle-slot-button 
          title="Scale Image to Original Size"         
          :btnClass="'primary ' + colorClass"
          @click="$emit('stopFitZoom'); $emit('updateZoom', 1);">1:1</circle-slot-button>
      </v-col>
      <v-col cols="auto">
        <grayscale-mode-button
          image="red-icon.png"
          title="Red"
          :fab="true"
          :grayscaleMode="grayscaleModes.Red"
          :selectedGrayscaleMode="grayscaleMode"
          @toggleGrayscaleMode="toggleGrayscaleMode(grayscaleModes.Red)"/>
      </v-col>
      <v-col cols="auto">
        <grayscale-mode-button
          image="green-icon.png"
          title="Green"
          :fab="true"
          :grayscaleMode="grayscaleModes.Green"
          :selectedGrayscaleMode="grayscaleMode"
          @toggleGrayscaleMode="toggleGrayscaleMode(grayscaleModes.Green)"/>
      </v-col>
      <v-col cols="auto" v-if="!isOptos">
        <grayscale-mode-button
          image="blue-icon.png"
          title="Blue"
          :fab="true"
          :grayscaleMode="grayscaleModes.Blue"
          :selectedGrayscaleMode="grayscaleMode"
          @toggleGrayscaleMode="toggleGrayscaleMode(grayscaleModes.Blue)"/>
      </v-col>
      <v-col cols="auto">
        <grayscale-mode-button
          image="bw-circle-icon.png"
          title="Monochrome"
          :fab="true"
          :grayscaleMode="grayscaleModes.REC709"
          :selectedGrayscaleMode="grayscaleMode"
          @toggleGrayscaleMode="toggleGrayscaleMode(grayscaleModes.REC709)"/>
      </v-col>
      <v-col cols="auto">
        <circle-image-button
          btnClass="primary"
          :active="rgbView"
          :title="rgbButtonTitle"
          :image="rgbButtonImage"
          @click="toggleRGB()"/>
      </v-col>
      <v-col cols="auto" v-if="rgbView">
        <circle-slot-button
          :active="rgbSync"
          :title="rgbSyncButtonTitle"
          text
          :btnClass="'primary ' + colorClass"
          @click="toggleRGBSync()">Sync</circle-slot-button>
      </v-col>
      <v-col cols="auto">
        <circle-image-button
          :active="isDragLock"
          :image="dragIcon"
          title="Drag"
          btnClass="primary"
          @click="dragButtonClick()"/>
      </v-col>
    </v-row>
    <reset-slider
      :value="brightness"
      :color="colorHex"
      @input="updateBrightness($event)"
      :min="-0.5"
      :max="0.5"
      :step="0.05"
      :defaultValue="defaultBrightness"
      name="Brightness"
      :limit="true"/>
    <reset-slider
      :value="contrast"
      :color="colorHex"
      @input="updateContrast($event)"
      :min="0.5"
      :max="1.5"
      :step="0.05"
      :defaultValue="defaultContrast"
      name="Contrast"
      :limit="true"/>
    <reset-slider
      :value="gamma"
      :color="colorHex"
      @input="updateGamma($event)"
      :min="0.1"
      :max="1.9"
      :step="0.05"
      :defaultValue="defaultGamma"
      name="Gamma"
      :limit="true"/>
    <v-layout v-if="isOptos && rgbView">
      <v-flex md6 class="green-label" style="text-align:center;">Green Channel</v-flex>
      <v-flex md6 style="text-align:center;">
        <circle-slot-button
          :active="greenSync"
          title="Sync Green Channel"
          :btnClass="'primary ' + colorClass"
          text
          @click="toggleGreenSync()">Sync</circle-slot-button>
      </v-flex>
    </v-layout>
    <reset-slider
      v-if="isOptos && rgbView"
      :color="colorHex"
      class="green-slider"
      :value="greenBrightness"
      @input="updateGreenBrightness($event)"
      :min="-0.5"
      :max="0.5"
      :step="0.05"
      :defaultValue="defaultBrightness"
      name="Brightness"
      :limit="true"/>
    <reset-slider
      v-if="isOptos && rgbView"
      :color="colorHex"
      class="green-slider"
      :value="greenContrast"
      @input="updateGreenContrast($event)"
      :min="0.5"
      :max="1.5"
      :step="0.05"
      :defaultValue="defaultContrast"
      name="Contrast"
      :limit="true"/>
    <v-row no-gutters justify="center">
      <v-col no-gutters cols="6">
          <annotation-mode-button
            :image="this.coloredAssetUrls.arrow"
            :activeImage="activeAnnotationIcons.arrow"
            title="Arrow"
            @click="annotationModeButtonClick(annotationModes.Arrow)"
            :buttonMode="annotationModes.Arrow"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.text"
            :activeImage="activeAnnotationIcons.text"
            title="Text"
            @click="annotationModeButtonClick(annotationModes.Text)"
            :buttonMode="annotationModes.Text"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.redacted"
            :activeImage="activeAnnotationIcons.redacted"
            title="Redaction"
            @click="annotationModeButtonClick(annotationModes.Redact)"
            :buttonMode="annotationModes.Redact"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.outlined"
            :activeImage="activeAnnotationIcons.outlined"
            title="Outline Box"
            @click="annotationModeButtonClick(annotationModes.Outline)"
            :buttonMode="annotationModes.Outline"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.freehand"
            :activeImage="activeAnnotationIcons.freehand"
            title="Free Hand Tool"
            @click="annotationModeButtonClick(annotationModes.FreeHandLine)"
            :buttonMode="annotationModes.FreeHandLine"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.continuous"
            :activeImage="activeAnnotationIcons.continuous"
            title="Continuous Draw"
            @click="annotationModeButtonClick(annotationModes.ContinuousDraw)"
            :buttonMode="annotationModes.ContinuousDraw"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"
            :activeAnnotation="activeAnnotation"/>
          <annotation-mode-button
            :image="this.coloredAssetUrls.angle"
            :activeImage="activeAnnotationIcons.angle"
            title="Angle"
            @click="annotationModeButtonClick(annotationModes.Angle)"
            :buttonMode="annotationModes.Angle"
            :disabled="disableAnnotationButtons"
            :annotationMode="annotationMode"/>
      </v-col>

      <v-col cols="2.5">
          <label class="annotation-label">Shape Outline</label>
          <!--<label class="annotation-label">Shape Fill</label> -->
          <label class="annotation-label">Font Size</label>
      </v-col>
      <v-col cols="3.5">
        <div class="mb-1">
          <circle-image-button
            :btnClass="shapeOutlineButtonClass"
            title="Shape Outline"
            :btnColor="annotationColor"
            :image="this.coloredAssetUrls.shapeOutline"
            @click="openShapeOutlineDialog"/>
        </div>
        <!--
        <div class="mb-1">
          <circle-image-button
            :btnClass="shapeFillButtonClass()"
            title="Shape Fill"
            :btnColor="shapeFillColor"
            :image="coloredAssetUrls.shapeFill"
            @click="openShapeFillDialog" />
        </div>
        -->
        <div>
          <select-with-button
            :value="selectedTextSize"
            @input="updateTextSize"
            :items="textSizes"
            name="textSize"/>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ANNOTATION_MODES, GRAYSCALE_MODES, TEXT_SIZES } from "../../../config/config";
import circleImageButton from "../../controls/CircleImageButton";
import circleSlotButton from "../../controls/CircleSlotButton";
import resetSlider from "../../controls/ResetSlider";
import selectWithButton from "../../controls/SelectWithButton";
import imageAnnotationHelper from "../../mixins/ImageAnnotationHelper";
import annotationModeButton from "./AnnotationModeButton";
import grayscaleModeButton from "./GrayscaleModeButton";
import imageBrowserZoomControl from "./ImageBrowserZoomControl";

import {
  DEFAULT_BRIGHTNESS,
  DEFAULT_CONTRAST,
  DEFAULT_GAMMA
} from "../../../canvas/canvasUtils";
export default {
  name: "imageAnnotationControls",
  components: {
    circleImageButton,
    resetSlider,
    imageBrowserZoomControl,
    annotationModeButton,
    grayscaleModeButton,
    circleSlotButton,
    selectWithButton
  },
  props: ["zoomControlSize", "zoomControlFit", "transform", "mainViewCanvasSize"],
  mixins: [imageAnnotationHelper],
  data() {
    return {
      annotationModes: ANNOTATION_MODES,
      grayscaleModes: GRAYSCALE_MODES,
      defaultBrightness: DEFAULT_BRIGHTNESS,
      defaultContrast: DEFAULT_CONTRAST,
      defaultGamma: DEFAULT_GAMMA,
      textSizes: TEXT_SIZES,
      items: [
        { title: 'Click Me1' },
        { title: 'Click Me2' },
        { title: 'Click Me3' },
        { title: 'Click Me4' },
      ],
      shapeFill: false
    };
  },
  computed: {
    ...mapGetters(["heightXs", "heightSm", "heightMd", "displayIsFullImage", "activeAnnotation"]),
    ...mapGetters({ "context": "imageBrowserContext"}),
    flipIcon() {
      return this.isFlipped
        ? this.activeAnnotationIcons.oneEightyDegree
        : this.coloredAssetUrls.oneEightyDegree;
    },
    flip90Icon() {
      return this.isFlipped90
        ? this.activeAnnotationIcons.nintyDegree
        : this.coloredAssetUrls.nintyDegree;
    },
    dragIcon() {
      return this.isDragLock
        ? this.activeAnnotationIcons.drag
        : this.coloredAssetUrls.drag;
    },
    rgbButtonTitle() {
      return this.isOptos ? "RG Channel View" : "RGB Channel View";
    },
    rgbButtonImage() {
      return this.isOptos
        ? "red-green-circles-icon.png"
        : "rgb-icon.png";
    },
    rgbSyncButtonTitle() {
      return this.isOptos ? "Sync RG Views" : "Sync RGB Views";
    },
    zoomControlHeight(){
      if (this.heightXs || this.heightSm || this.heightMd) return "100px";
      return "200px";
    },
    disableAnnotationButtons(){
      return !this.displayIsFullImage;
    },
    shapeOutlineButtonClass() {
      return "rounded-button shape-outline-button " + this.colorClass;
    }
  },
  methods: {
    currentMode(mode) {
      return this.annotationMode == mode;
    },
    openShapeOutlineDialog() 
    {
      this.$store.commit("setShapeOutlineSyncContext", null);
      this.$store.commit("setShapeOutlineContext", this.context);
    },
    openShapeFillDialog() 
    {
      this.$store.commit("setShapeFillSyncContext", null);
      this.$store.commit("setShapeFillContext", this.context);
    },
    shapeFillButtonClass(){
      let btnClass = "rounded-button annotation-color-button " + this.colorClass;
      if (this.annotationMode == ANNOTATION_MODES.ShapeFill) btnClass += " selected-color";
      return btnClass;
    }
  }
};
</script>

<style lang="scss" scoped>
.annotation-controls ::v-deep {
  .green-label {
    color: #339933;
    text-transform: uppercase;
    font-size: 17px;
  }

  .green-slider .slider-label {
    color: #339933;
  }
}
</style>
